import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { ArrowLeft, User, Shield, Key, CheckCircle, Clock, XCircle, Lock, LogOut, AlertTriangle, Mail, Building2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { supabase } from '@/lib/supabase'
import { useNavigate } from 'react-router-dom'
import {
  requestLicense,
  getUserLicenseRequests,
  getUserWhitelistedHardware,
  checkLicense,
  LicenseRequest,
  WhitelistedHardware
} from '@/lib/licensing'
import { getHardwareFingerprint } from '@/lib/hardwareId'
import { canManage, isSuperAdmin } from '@/lib/roles'
import { InvitesSection } from '@/components/settings/InvitesSection'
import { ShipyardsSection } from '@/components/settings/ShipyardsSection'
import { useTranslation } from 'react-i18next'

type TabKey = 'account' | 'license' | 'invitations' | 'shipyards'

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const { success, error } = useToast()
  const navigate = useNavigate()
  const isSuper = isSuperAdmin(user)
  // admin-or-super: the backend gates the same edits with requireAdmin
  const canManageShipyards = canManage(user)

  const [activeTab, setActiveTab] = useState<TabKey>('account')

  // license state
  const [pastedHardwareId, setPastedHardwareId] = useState<string>('')
  const [requesting, setRequesting] = useState(false)
  const [licenseRequests, setLicenseRequests] = useState<LicenseRequest[]>([])
  const [whitelistedHardware, setWhitelistedHardware] = useState<WhitelistedHardware[]>([])
  const [licenseStatus, setLicenseStatus] = useState<{ isLicensed: boolean; message: string }>({
    isLicensed: false,
    message: t('settingsPage.checking')
  })

  // password change
  const [changingPassword, setChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // auto-lock timer (client-side idle logout, not a server session revoke)
  const [autoLockMinutes, setAutoLockMinutes] = useState<number>(30)

  useEffect(() => {
    void loadData()
    const saved = localStorage.getItem('sessionTimeout')
    if (saved) setAutoLockMinutes(parseInt(saved))
    localStorage.setItem('lastActivity', Date.now().toString())
  }, [])

  // auto-lock: client-side idle timer. resets on user activity. calls
  // signOut() after autoLockMinutes minutes of no input. doesn't revoke
  // refresh tokens server-side, purely local UX, useful for unattended
  // workstations but not a security boundary
  useEffect(() => {
    const handleActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString())
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(ev => document.addEventListener(ev, handleActivity))

    const timeoutCheck = setInterval(() => {
      const last = parseInt(localStorage.getItem('lastActivity') || Date.now().toString())
      const since = Date.now() - last
      if (since > autoLockMinutes * 60 * 1000) {
        alert(t('settingsPage.sessionExpired'))
        signOut()
      }
    }, 60000)

    return () => {
      events.forEach(ev => document.removeEventListener(ev, handleActivity))
      clearInterval(timeoutCheck)
    }
  }, [autoLockMinutes, signOut, t])

  const handleAutoLockChange = (minutes: number) => {
    setAutoLockMinutes(minutes)
    localStorage.setItem('sessionTimeout', minutes.toString())
    success(t('settingsPage.autoLockUpdated'), t('settingsPage.autoLockUpdatedDetail', { minutes }))
  }

  const loadData = async () => {
    try {
      await getHardwareFingerprint()  // warms cache, results unused on this page now
      const status = await checkLicense()
      setLicenseStatus(status)
      setLicenseRequests(await getUserLicenseRequests())
      setWhitelistedHardware(await getUserWhitelistedHardware())
    } catch (err) {
      console.error('Error loading license data:', err)
    }
  }

  const handleRequestLicense = async () => {
    if (!pastedHardwareId || pastedHardwareId.trim().length === 0) {
      error(t('notifications.error'), t('settingsPage.hardwareIdRequired'))
      return
    }
    const normalizedId = pastedHardwareId.trim().toUpperCase()
    if (normalizedId.length < 16) {
      error(t('notifications.error'), t('settingsPage.hardwareIdInvalid'))
      return
    }
    setRequesting(true)
    try {
      const result = await requestLicense(normalizedId)
      if (result.success) {
        success(t('settingsPage.licenseSubmitted'), result.message)
        setPastedHardwareId('')
        await loadData()
      } else {
        error(t('settingsPage.requestFailed'), result.message)
      }
    } catch (err) {
      error(t('notifications.error'), t('settingsPage.licenseSubmitFailed'))
    } finally {
      setRequesting(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      error(t('notifications.error'), t('settingsPage.fillPasswordFields'))
      return
    }
    if (newPassword !== confirmPassword) {
      error(t('notifications.error'), t('settingsPage.passwordsMismatch'))
      return
    }
    if (newPassword.length < 8) {
      error(t('notifications.error'), t('settingsPage.passwordMin'))
      return
    }
    setChangingPassword(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        currentPassword,
      })
      if (updateError) throw updateError
      success(t('settingsPage.passwordUpdated'), t('settingsPage.passwordUpdatedDetail'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      error(t('notifications.error'), err.message || t('settingsPage.passwordChangeFailed'))
    } finally {
      setChangingPassword(false)
    }
  }

  const tabs: { key: TabKey; label: string; icon: React.ReactNode; show: boolean }[] = [
    { key: 'account',     label: t('settingsPage.tabs.account'),     icon: <User className="h-4 w-4" />,  show: true },
    { key: 'license',     label: t('settingsPage.tabs.license'),     icon: <Key className="h-4 w-4" />,   show: true },
    { key: 'invitations', label: t('settingsPage.tabs.invitations'), icon: <Mail className="h-4 w-4" />,  show: isSuper },
    { key: 'shipyards',   label: t('settingsPage.tabs.shipyards'),   icon: <Building2 className="h-4 w-4" />, show: canManageShipyards },
  ]

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)} className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg sm:text-xl font-semibold">{t('settings.title')}</h1>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">

          {/* Tab bar */}
          <nav className="flex gap-1 border-b border-slate-200 dark:border-slate-700 mb-6" role="tablist">
            {tabs.filter(t => t.show).map(tab => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Account tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">

              {/* Account information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <User className="h-5 w-5 text-slate-500" />
                    {t('settingsPage.accountInformation')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Email</span>
                      <span className="font-medium">{user?.email || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">{t('settingsPage.userId')}</span>
                      <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-xs">
                        {user?.id?.substring(0, 16) || '—'}…
                      </code>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Change password */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Lock className="h-5 w-5 text-slate-500" />
                    {t('settingsPage.changePassword')}
                  </CardTitle>
                  <CardDescription>{t('settingsPage.changePasswordDescription')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                      {t('settingsPage.currentPassword')}
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                      {t('settingsPage.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      placeholder={t('settingsPage.passwordPlaceholder')}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                      {t('settingsPage.confirmNewPassword')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                    className="w-full"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {changingPassword ? t('settingsPage.updatingPassword') : t('settingsPage.changePassword')}
                  </Button>
                </CardContent>
              </Card>

              {/* Auto-lock timer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="h-5 w-5 text-slate-500" />
                    {t('settingsPage.autoLock')}
                  </CardTitle>
                  <CardDescription>
                    {t('settingsPage.autoLockDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-slate-700 dark:text-slate-300">{t('settingsPage.lockAfter')}</label>
                    <select
                      value={autoLockMinutes}
                      onChange={(e) => handleAutoLockChange(parseInt(e.target.value))}
                      className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-sm"
                    >
                      <option value={5}>{t('settingsPage.minutes', { count: 5 })}</option>
                      <option value={15}>{t('settingsPage.minutes', { count: 15 })}</option>
                      <option value={30}>{t('settingsPage.minutes', { count: 30 })}</option>
                      <option value={60}>{t('settingsPage.hours', { count: 1 })}</option>
                      <option value={120}>{t('settingsPage.hours', { count: 2 })}</option>
                      <option value={240}>{t('settingsPage.hours', { count: 4 })}</option>
                      <option value={480}>{t('settingsPage.hours', { count: 8 })}</option>
                    </select>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {t('settingsPage.autoLockWarning')}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Sign out */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Shield className="h-5 w-5 text-slate-500" />
                    {t('auth.signout')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {t('settingsPage.signOutDescription')}
                    </p>
                    <Button
                      onClick={() => { if (confirm(t('settingsPage.confirmSignOut'))) signOut() }}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 border-red-300 flex-shrink-0"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('auth.signout')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>
          )}

          {/* License tab */}
          {activeTab === 'license' && (
            <div className="space-y-6">

              {/* License status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Key className="h-5 w-5 text-slate-500" />
                    {t('settingsPage.hardwareLicense')}
                  </CardTitle>
                  <CardDescription>{t('settingsPage.hardwareLicenseDescription')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t('fields.status')}</span>
                    {licenseStatus.isLicensed ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                        <CheckCircle className="h-4 w-4" /> {t('settingsPage.licensed')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400 text-sm font-medium">
                        <Clock className="h-4 w-4" /> {t('settingsPage.notLicensed')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{licenseStatus.message}</p>
                </CardContent>
              </Card>

              {/* Request new license */}
              {!licenseStatus.isLicensed && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('settingsPage.requestAccess')}</CardTitle>
                    <CardDescription>
                      {t('settingsPage.requestAccessDescription')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <input
                      type="text"
                      value={pastedHardwareId}
                      onChange={(e) => setPastedHardwareId(e.target.value)}
                      placeholder="e.g. ABC123DEF456..."
                      autoComplete="off"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-mono text-sm"
                    />
                    <Button
                      onClick={handleRequestLicense}
                      disabled={requesting || !pastedHardwareId.trim()}
                      className="w-full"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      {requesting ? t('settingsPage.submitting') : t('settingsPage.requestLicense')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Request history */}
              {licenseRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('settingsPage.yourRequests')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {licenseRequests.map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded">
                        <div className="flex-1">
                          <div className="text-sm font-medium">{req.machine_name || t('settingsPage.unknownMachine')}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {t('settingsPage.requestedOn', { date: new Date(req.requested_at).toLocaleDateString() })}
                          </div>
                        </div>
                        {req.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
                            <CheckCircle className="h-3.5 w-3.5" /> {t('settingsPage.approved')}
                          </span>
                        )}
                        {req.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs">
                            <Clock className="h-3.5 w-3.5" /> {t('settingsPage.pending')}
                          </span>
                        )}
                        {req.status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
                            <XCircle className="h-3.5 w-3.5" /> {t('settingsPage.rejected')}
                          </span>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Active licenses */}
              {whitelistedHardware.filter(h => h.is_active).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('settingsPage.activeLicenses')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {whitelistedHardware.filter(h => h.is_active).map((hw) => (
                      <div key={hw.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded">
                        <div>
                          <div className="text-sm font-medium">{hw.machine_name || t('settingsPage.unknownMachine')}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {t('settingsPage.approvedOn', { date: new Date(hw.approved_at).toLocaleDateString() })}
                            {hw.last_seen_at && <> · {t('settingsPage.lastSeen', { date: new Date(hw.last_seen_at).toLocaleDateString() })}</>}
                          </div>
                        </div>
                        <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

            </div>
          )}

          {/* Invitations tab */}
          {activeTab === 'invitations' && isSuper && (
            <InvitesSection />
          )}

          {/* Shipyards tab */}
          {activeTab === 'shipyards' && canManageShipyards && (
            <ShipyardsSection />
          )}

        </div>
      </div>
    </div>
  )
}
